"use client";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { PresetTypeSlug } from "@/features/presets";
import type { AutocompleteResult, FilterValue } from "@/lib/api/types";
import type { IconName } from "@/components/ui/icon";
import { legacyFundingStageScope } from "@/features/search/lib/filter-scope";
import { REVENUE_RANGES } from "@/features/search/lib/funding-filters";
import { FilterFundingRangeInput } from "@/features/search/components/filters/input/funding-range-input";
import { PresetButton } from "../shared";
import { ScopedListHeader, ScopedValueList, toggleScopedValue } from "./scoped-list";

const FUNDING_STAGES: { value: string; label: string; icon: IconName }[] = [
  { value: "angel", label: "Angel", icon: "IconCoin1" },
  { value: "pre_seed", label: "Pre-Seed", icon: "IconGrowth" },
  { value: "seed", label: "Seed", icon: "IconBlossom" },
  { value: "series_a", label: "Series A", icon: "IconPush" },
  { value: "series_b", label: "Series B", icon: "IconDiamond" },
  { value: "series_c", label: "Series C", icon: "IconFire2" },
  { value: "series_d", label: "Series D", icon: "IconUnicorn" },
  { value: "series_e", label: "Series E", icon: "IconTrophy" },
  { value: "series_f", label: "Series F", icon: "IconWizardHat" },
  { value: "ipo", label: "IPO", icon: "IconCrown" },
  { value: "debt_financing", label: "Debt Financing", icon: "IconMoneyHand" },
  { value: "grant", label: "Grant", icon: "IconHeartDonation" },
  { value: "private_equity", label: "Private Equity", icon: "IconGold" },
  { value: "secondary_market", label: "Secondary Market", icon: "IconStocks" },
  { value: "convertible_note", label: "Convertible Note", icon: "IconWreath" },
];

const FUNDING_STAGE_META: Record<string, { label: string; icon: IconName }> = Object.fromEntries(
  FUNDING_STAGES.map((s) => [s.value, { label: s.label, icon: s.icon }]),
);

export function FilterFundingSection({ onOpenPreset }: { onOpenPreset?: (type: PresetTypeSlug) => void }) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const stages = filters.company_funding_stages ?? [];
  const investors = filters.investors ?? [];
  const revenueRanges = filters.revenue_ranges ?? [];
  const stageFallbackScope = legacyFundingStageScope(filters);
  const investorFallbackScope = filters.investor_filter_type ?? "cp";

  // Per-chip scopes replace the retired section-level `funding_scope`; once
  // the stages are rewritten with pinned scopes it no longer applies.
  const writeStages = (next: FilterValue[] | undefined) => {
    updateFilter("company_funding_stages", next);
    if (filters.funding_scope !== undefined) updateFilter("funding_scope", undefined);
  };

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <ScopedListHeader
          label="Company Funding Stages"
          items={stages}
          fallbackScope={stageFallbackScope}
          onChange={writeStages}
        />
        <AutocompleteInput
          variant="filter"
          field="company_funding_stages"
          placeholder="Search..."
          staticItems={FUNDING_STAGES}
          selectedValues={stages.map((fs) => fs.text).filter((t): t is string => t != null)}
          allowFreeText={false}
          onSelect={(result: AutocompleteResult) => {
            const next = toggleScopedValue(stages, result.value, stageFallbackScope);
            writeStages(next.length > 0 ? next : undefined);
          }}
        />
      </div>
      <ScopedValueList
        items={stages}
        fallbackScope={stageFallbackScope}
        getLabel={(fs) => FUNDING_STAGE_META[fs.text ?? ""]?.label ?? fs.text ?? ""}
        getIcon={(fs) => FUNDING_STAGE_META[fs.text ?? ""]?.icon}
        onChange={writeStages}
      />

      <div className="flex flex-col gap-1 w-full">
        <ScopedListHeader
          label="Investor(s)"
          items={investors}
          fallbackScope={investorFallbackScope}
          onChange={(next) => updateFilter("investors", next)}
        />
        <div className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field="investors"
            placeholder="Search..."
            flatBottom
            onSelect={(result: AutocompleteResult) => {
              if (!investors.some((i) => i.text === result.value)) {
                updateFilter("investors", [...investors, { text: result.value, required: false, scope: "cp" }]);
              }
            }}
            onFreeText={(text: string) => {
              updateFilter("investors", [...investors, { text: text.toLowerCase(), required: false, scope: "cp" }]);
            }}
          />
          <PresetButton label="Investor Presets" onClick={() => onOpenPreset?.("investors")} />
        </div>
      </div>
      <ScopedValueList
        items={investors}
        fallbackScope={investorFallbackScope}
        onChange={(next) => updateFilter("investors", next)}
      />

      <div className="flex flex-col gap-1 w-full">
        <ScopedListHeader
          label="Company Revenue"
          items={revenueRanges}
          onChange={(next) => updateFilter("revenue_ranges", next)}
        />
        <AutocompleteInput
          variant="filter"
          field="revenue_ranges"
          placeholder="Search..."
          staticItems={REVENUE_RANGES}
          selectedValues={revenueRanges.map((r) => r.text).filter((t): t is string => t != null)}
          allowFreeText={false}
          onSelect={(result: AutocompleteResult) => {
            const next = toggleScopedValue(revenueRanges, result.value);
            updateFilter("revenue_ranges", next.length > 0 ? next : undefined);
          }}
        />
      </div>
      <ScopedValueList
        items={revenueRanges}
        getIcon={() => "IconMoneybag"}
        onChange={(next) => updateFilter("revenue_ranges", next)}
      />

      <FilterFundingRangeInput
        min={filters.min_funding ?? undefined}
        max={filters.max_funding ?? undefined}
        onRangeChange={(min, max) => {
          if (min !== (filters.min_funding ?? undefined)) updateFilter("min_funding", min);
          if (max !== (filters.max_funding ?? undefined)) updateFilter("max_funding", max);
        }}
      />
    </>
  );
}
